import { API_BASE_URL, instance } from "./index";
import {
  createCanvasImageRef,
  getCanvasImageBlobName,
  isCanvasImageRef,
} from "../features/canvas/canvasImageRefs";

const CANVAS_IMAGE_ENDPOINT = "/api/notes/canvas-image";
const CANVAS_IMAGE_FIELD = "image";
const CANVAS_IMAGE_NAME_QUERY_KEY = "name";
const UPLOAD_FILE_BASE_NAME = "canvas-image";

const dataUrlToBlob = async (dataUrl) => {
  const response = await fetch(dataUrl);
  return response.blob();
};

const buildUploadFileName = (imageBlob) => {
  const subtype = String(imageBlob.type).split("/")[1] || "png";
  return `${UPLOAD_FILE_BASE_NAME}.${subtype}`;
};

/**
 * @param {string} blobName
 * @returns {string} stable, cacheable delivery URL for a canvas image blob
 */
export const buildCanvasImageDeliveryUrl = (blobName) => {
  const query = new URLSearchParams({
    [CANVAS_IMAGE_NAME_QUERY_KEY]: blobName,
  });
  return `${API_BASE_URL}${CANVAS_IMAGE_ENDPOINT}?${query.toString()}`;
};

/**
 * Maps a persisted element src to something an HTMLImageElement can load.
 * Data URLs and plain URLs pass through untouched.
 * @param {string} src
 * @returns {string}
 */
export const resolveCanvasImageSrc = (src) =>
  isCanvasImageRef(src)
    ? buildCanvasImageDeliveryUrl(getCanvasImageBlobName(src))
    : src;

/**
 * Uploads an in-memory data URL image and returns its stable asset ref.
 * @param {string} dataUrl
 * @returns {Promise<string>} `netpad-blob:<blobName>` ref
 * @throws {Error} when the upload fails or returns no blob name
 */
export const uploadCanvasImageFromDataUrl = async (dataUrl) => {
  const imageBlob = await dataUrlToBlob(dataUrl);
  const formData = new FormData();
  formData.append(CANVAS_IMAGE_FIELD, imageBlob, buildUploadFileName(imageBlob));

  const response = await instance.post(CANVAS_IMAGE_ENDPOINT, formData);
  const blobName = response.data?.data?.blobName;
  if (!blobName) {
    throw new Error("Canvas image upload returned no blob name.");
  }

  return createCanvasImageRef(blobName);
};

/**
 * Warms the browser HTTP cache for an uploaded image so swapping the element
 * src never leaves the scene pointing at an unfetchable ref.
 * @param {string} assetRef
 * @returns {Promise<void>}
 */
export const preloadCanvasImage = (assetRef) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve();
    image.onerror = () =>
      reject(new Error(`Canvas image preload failed for ${assetRef}.`));
    image.src = resolveCanvasImageSrc(assetRef);
  });
